import React, { ReactElement } from 'react'
import dayjs from 'dayjs'
import { Box, Divider, Typography } from '@mui/material'

import {
  ANCCAccreditation,
  CAPCEAccreditationV2,
  QuestionData,
  QuizV2,
} from 'src/v3/core/types'
import { CloseIcon, ConfirmIcon } from 'src/v3/core/icons'
import {
  onQuestionImageError,
  questionImageSrc,
} from 'src/v3/components/quiz/utils/question-image'

export let QuizApproval = ({
  quiz,
  password,
}: {
  quiz: QuizV2 & { questions: QuestionData[] }
  password: string
}): ReactElement => (
  <Box sx={{ py: 0.5 }}>
    <Typography>
      <strong>Password</strong>: {password}
    </Typography>
    <Typography>
      <strong>Passing Score</strong>: {quiz.passingScore} /{' '}
      {quiz.questions.length} Questions
    </Typography>
    <Typography>
      <strong>Randomization</strong>:{' '}
      {quiz.randomizeQuestions
        ? `Randomized Questions \u2022 ${quiz.displayedQuestions} Questions Displayed`
        : 'Static Questions'}
    </Typography>
    {quiz.questions.map((qq, i) => (
      <Box key={qq.quizQuestion.id}>
        <Divider sx={{ my: 1.5 }} />
        <Typography
          sx={{
            fontWeight: 'bold',
          }}
        >
          Question {i + 1}
        </Typography>
        <Typography sx={{ mt: 0.5 }}>{qq.quizQuestion.text}</Typography>
        <Box>
          {qq.quizQuestion?.imageName && (
            <img
              style={{ maxHeight: 180, border: '1px solid #6d6d6d' }}
              src={questionImageSrc(qq.quizQuestion.imageName)}
              onError={(event) =>
                onQuestionImageError(event, qq.quizQuestion.imageName)
              }
              alt={`Question ${i + 1} media`}
            />
          )}
        </Box>
        <Box sx={{ my: 1 }}>
          {qq.questionAnswers.map((qa) => (
            <Box
              key={qa.id}
              sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
            >
              {qa.id === qq.correctAnswer.answerID ? (
                <ConfirmIcon color="success" />
              ) : (
                <CloseIcon color="error" />
              )}
              <Typography>{qa.text}</Typography>
            </Box>
          ))}
        </Box>
        <Typography>
          <strong>Explanation</strong>: {qq.quizQuestion.explanation}
        </Typography>
      </Box>
    ))}
  </Box>
)

export let ANCCApproval = ({
  accreditation,
}: {
  accreditation: ANCCAccreditation
}): ReactElement => (
  <Box sx={{ py: 0.5, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
    <Typography>
      <strong>Course Code</strong>: {accreditation.courseCode}
    </Typography>
    <Typography>
      <strong>Expiration Date</strong>:{' '}
      {dayjs(accreditation.expirationDate).format('MM/DD/YYYY')}
    </Typography>
    <Typography>
      <strong>Target Audience</strong>:{' '}
      {accreditation.targetAudience.join(', ')}
    </Typography>
    <Typography>
      <strong>Educational Needs</strong>:{' '}
      {accreditation.educationalNeeds.join(', ')}
    </Typography>
    <Typography>
      <strong>Competency</strong>: {accreditation.competency}
    </Typography>
    <Typography>
      <strong>Professional Source</strong>: {accreditation.professionalSource}
    </Typography>
  </Box>
)

export let CAPCEApproval = ({
  accreditation,
}: {
  accreditation: CAPCEAccreditationV2
}): ReactElement => (
  <Box sx={{ py: 0.5, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
    <Typography>
      <strong>Course Code</strong>: {accreditation.courseCode}
    </Typography>
    <Typography>
      <strong>Expiration Date</strong>:{' '}
      {accreditation.expirationDate
        ? dayjs(accreditation.expirationDate).format('MM/DD/YYYY')
        : 'None'}
    </Typography>
    <Typography>
      <strong>Application Date</strong>:{' '}
      {accreditation.applicationDate
        ? dayjs(accreditation.applicationDate).format('MM/DD/YYYY')
        : 'None'}
    </Typography>
    <Typography>
      <strong>Submission Date</strong>:{' '}
      {accreditation.submissionDate
        ? dayjs(accreditation.submissionDate).format('MM/DD/YYYY')
        : 'None'}
    </Typography>
    <Typography>
      <strong>Topic Area</strong>: {accreditation.topicArea}
    </Typography>
    <Typography>
      <strong>Class Type</strong>: {accreditation.classType}
    </Typography>
    <Typography>
      <strong>All Clear Date</strong>:{' '}
      {accreditation.allClearDate
        ? dayjs(accreditation.allClearDate).format('MM/DD/YYYY')
        : 'None'}
    </Typography>
    <Typography>
      <strong>Go Live Date</strong>:{' '}
      {accreditation.goLiveDate
        ? dayjs(accreditation.goLiveDate).format('MM/DD/YYYY')
        : 'None'}
    </Typography>
  </Box>
)
